export const SITE_URL = 'https://www.meduler.com';

export const ROUTE_REGISTRY = [
  {
    path: '/',
    title: 'Meduler | Smart Medication Management App',
    description: 'Effortlessly manage your medications with Meduler. Intelligent scheduling, seamless sharing, and intuitive medication tracking for patients and caregivers.',
    priority: 1.0,
    changefreq: 'weekly',
    h1: 'Smart Medication Management for Patients and Caregivers',
    intro: 'Meduler is a privacy-first medication management app that helps you stay on top of your health. Track medications, set smart reminders, and share care with family members — all offline-first with AES-256 encryption.',
    content: 'Meduler helps individuals and caregivers organize medication routines without turning health management into another source of stress. Add medications, record dosage and timing details, set reminders that fit daily life, and review a clear history of doses. The free experience is designed to be useful from the first day, with no account required to begin. Meduler is built offline-first so the schedule and dose log remain available when a connection is unreliable. Caregivers can use separate profiles and share only the information a loved one chooses to share. Meduler is a personal organization tool, not a medical device or a replacement for a doctor, pharmacist, or emergency service. Verify medication instructions with a qualified healthcare professional and keep a backup plan for important doses. Start with the free app, learn about the features, or read the privacy policy before deciding whether Meduler fits your routine.',
  },
  {
    path: '/features',
    title: 'Features | Meduler',
    description: "Explore Meduler's powerful features: smart medication reminders, caregiver mode, offline-first storage, adherence tracking, and AES-256 encrypted data.",
    priority: 0.9,
    changefreq: 'monthly',
    h1: 'Powerful Features Built Around Your Health',
    intro: 'From intelligent reminders to caregiver sharing, Meduler delivers everything you need to manage medications confidently. All data stays private with AES-256 encryption and full offline support.',
    content: 'Meduler combines the practical tools people use every day to manage a medication routine. Create a schedule for each medication, choose reminder times, and log doses as taken, missed, or skipped. The schedule view keeps the next action visible while history and adherence views help you notice patterns over time. Caregiver features support a second profile and controlled sharing for families who coordinate care. Offline-first operation means core schedule and logging features can continue without Wi-Fi or mobile data. Privacy is part of the product design: Meduler does not sell health data, and sharing is controlled by the person managing the profile. Meduler does not diagnose conditions, verify prescriptions, check every drug interaction, or replace clinical records. Read the full feature details, see how setup works, and compare the free and Plus plans before getting started.',
  },
  {
    path: '/how-it-works',
    title: 'How It Works | Meduler',
    description: 'Learn how Meduler works: add your medications, set personalized reminders, track adherence, and share with caregivers — in just a few steps.',
    priority: 0.8,
    changefreq: 'monthly',
    h1: 'How Meduler Works',
    intro: 'Getting started with Meduler takes minutes. Add your medications, configure your schedule, and let Meduler handle the reminders. Caregivers can be invited to view and support your regimen at any time.',
    content: 'Getting started with Meduler follows a simple sequence. First, enter each medication, including the name, dose, form, and any notes that help you recognize it. Next, choose a schedule that matches the instructions from your healthcare professional and your normal day. Enable reminders if notifications are useful, then confirm doses from the daily schedule as you take them. Over time, the history and adherence views provide an organized record that you can review or discuss with your care team. A caregiver can use a separate profile or receive access to information you intentionally share. The app is designed to keep core work available offline, but it should not be your only safeguard for a critical medication. Always verify the regimen with a doctor or pharmacist, and use the setup guide to understand what Meduler can and cannot do.',
  },
  {
    path: '/caregivers',
    title: 'Caregiver Mode | Meduler',
    description: "Meduler's caregiver mode lets family members and care professionals monitor and support medication adherence for loved ones — privately and securely.",
    priority: 0.8,
    changefreq: 'monthly',
    h1: 'Supporting Caregivers and Families',
    intro: "Meduler's caregiver mode is designed for families and care professionals. Get real-time visibility into a loved one's medication schedule, set up shared reminders, and stay informed — all with end-to-end privacy.",
    content: 'Meduler gives families a practical way to coordinate medication routines while respecting the independence of the person taking the medication. A caregiver can create or switch to a separate profile, record a loved one’s schedule, and use a shared view when the primary user has chosen to grant access. Clear dose history helps family members discuss questions with the right professional instead of relying on memory. Offline-first support is useful when a caregiver or loved one has limited connectivity. Caregiver tools are intended for personal coordination and are not a substitute for clinical care, a pharmacy system, or emergency monitoring. Meduler does not independently confirm that a dose was taken, provide medical advice, or make decisions about treatment. Learn how caregiver mode works, review the privacy controls, and choose the free or Plus capabilities that match the care arrangement.',
  },
  {
    path: '/pricing',
    title: 'Pricing | Meduler',
    description: 'Meduler is free to use. Upgrade to Meduler Plus for advanced analytics, extended history, priority support, and more. Starting at $4.99/month.',
    priority: 0.8,
    changefreq: 'monthly',
    h1: 'Simple, Transparent Pricing',
    intro: 'Meduler offers a generous free tier with core medication management features. Meduler Plus unlocks advanced adherence insights, unlimited medication history, and priority customer support for $4.99 per month.',
    content: 'Meduler starts with a free tier that covers the core medication routine: medication and schedule organization, reminders, dose logging, offline access, and basic caregiver support. Meduler Plus is listed at $4.99 per month and is intended for people who want additional adherence insights, longer history, expanded sharing, custom reminder patterns, and priority customer support. Review the current plan details on this page before joining the Plus waitlist, because availability and included features can change as the product develops. There is no need to pay to evaluate the basic workflow. Meduler is a personal organization tool and does not include medical advice, prescription fulfillment, or a guarantee that a reminder will be delivered. Ask a healthcare professional about treatment decisions, then use Meduler to organize the instructions you have received.',
  },
  {
    path: '/signup',
    title: 'Join the Meduler Plus Waitlist',
    description: 'Join the Meduler Plus waitlist to hear when expanded medication insights, history, sharing, and support features become available.',
    priority: 0.6,
    changefreq: 'monthly',
    h1: 'Join the Meduler Plus Waitlist',
    intro: 'Meduler Plus is in development. Join the waitlist to receive product availability updates without making a payment.',
    content: 'The Meduler Plus waitlist is for people who want to hear when expanded medication-management features become available. Plus is listed at $4.99 per month and is planned to include longer medication history, additional adherence insights, expanded family sharing, custom reminder patterns, and priority product support. Joining the waitlist does not start a subscription and does not require payment. Review the current pricing page before joining because availability, timing, and included features may change as the product develops. The free Meduler experience remains available for core medication scheduling, reminders, dose logging, offline access, and basic caregiver organization. Use the waitlist only for product updates and do not submit medication names, diagnoses, prescriptions, or other sensitive health information. Meduler is a personal organization tool, not a medical device or a source of medical advice. Ask a qualified doctor or pharmacist about treatment decisions.',
  },
  {
    path: '/about',
    title: 'About Us | Meduler',
    description: "Learn about Meduler's mission to make medication management simple, private, and accessible for everyone — patients and caregivers alike.",
    priority: 0.7,
    changefreq: 'yearly',
    h1: 'Our Mission: Medication Management for Everyone',
    intro: 'Meduler was built by a team passionate about health technology and privacy. Our mission is to make medication adherence simple and stress-free, with a product that respects your data and works even without an internet connection.',
    content: 'Meduler’s mission is to make medication organization simpler, calmer, and more respectful of people’s privacy. The product focuses on everyday tasks: keeping a schedule, remembering dose times, recording what happened, and making it easier for a trusted caregiver to help when the user wants support. The team’s product philosophy is to design for real life, including busy routines, shared responsibilities, and moments when internet access is limited. Meduler is not presented as a medical device, a source of treatment advice, or a replacement for the relationship between a patient and a qualified healthcare professional. We aim to be clear about those boundaries while building useful software. Learn about the product through the feature and how-it-works pages, review the privacy policy, or contact the team with questions about the public service.',
  },
  {
    path: '/contact',
    title: 'Contact Meduler',
    description: 'Contact Meduler with questions about the medication management app, privacy, accessibility, or support.',
    priority: 0.6,
    changefreq: 'yearly',
    h1: 'Contact Meduler',
    intro: 'Questions about Meduler, privacy, accessibility, or the Plus waitlist? The public support channels below are the best place to start.',
    content: 'The Meduler team welcomes questions about the public website and medication management app. For product support, accessibility questions, privacy requests, or questions about the Meduler Plus waitlist, email support@meduler.com. For a privacy-specific request, include enough context for the team to understand the request, but do not send medication names, diagnoses, prescriptions, or other sensitive health information by email. Meduler support cannot provide medical advice, diagnose a condition, change a prescription, or tell you whether to take a dose. Contact a doctor or pharmacist for those questions, and use local emergency services for an urgent medical situation. Before writing, you may find an answer in the FAQ, features, pricing, privacy policy, or medical disclaimer pages. The contact page describes the available public channel and does not expose private account data or application screens.',
  },
  {
    path: '/faq',
    title: 'FAQ | Meduler',
    description: 'Frequently asked questions about Meduler — how it works, data privacy, caregiver features, offline mode, and Meduler Plus.',
    priority: 0.7,
    changefreq: 'monthly',
    h1: 'Frequently Asked Questions',
    intro: 'Find answers to the most common questions about Meduler, including how your data is stored, how caregiver sharing works, what Meduler Plus includes, and how offline mode keeps you covered.',
    content: 'The Meduler FAQ explains the questions people commonly ask before using the app. It covers the free tier, Meduler Plus, reminders, dose logging, caregiver profiles, offline operation, privacy, and the limits of the product. Meduler is intended to help organize information supplied by the user; it does not replace a prescriber or pharmacist and it is not a clinical record. Core data is designed to remain available locally on the device, while any sharing should be intentional and limited to the people the user chooses. Because device settings, connectivity, and notification permissions can affect reminders, keep a backup method for important medication timing. If the FAQ does not answer your question, contact the public support channel without sending unnecessary health information. The feature, how-it-works, pricing, privacy, and disclaimer pages provide more detail.',
  },
  {
    path: '/get-the-app',
    title: 'Get the App | Meduler',
    description: 'Download Meduler on iOS and Android, or use it as a Progressive Web App (PWA) directly in your browser — no installation required.',
    priority: 0.8,
    changefreq: 'monthly',
    h1: 'Get Meduler on Your Device',
    intro: 'Meduler is available as a Progressive Web App (PWA) that works on any device. Install it on your iPhone, Android phone, or desktop browser for a native app experience with full offline support.',
    content: 'Meduler is delivered as a Progressive Web App, so you can open it in a supported browser and optionally add it to your device’s home screen. On iPhone, use the browser share menu and choose Add to Home Screen. On Android, use the browser menu and choose Install App when the option is available. Desktop browsers may show an install control in the address bar or browser menu. Installation is optional: the web app can still be opened directly. Browser notification permissions, battery settings, and operating-system policies can affect reminders, so review those settings and keep a backup for time-critical doses. Meduler is free to start, works offline for core organization tasks, and does not replace medical advice. Read the privacy policy before installing and visit the support page if you need help.',
  },
  {
    path: '/terms',
    title: 'Terms of Service | Meduler',
    description: "Read Meduler's Terms of Service. Understand your rights and responsibilities when using the Meduler medication management platform.",
    priority: 0.4,
    changefreq: 'yearly',
    h1: 'Terms of Service',
    intro: 'These terms govern your use of the Meduler platform. Please read them carefully before using our service.',
    content: 'These Terms of Service describe the basic expectations for using Meduler. The service is provided as a personal medication organization tool: users are responsible for entering accurate information, checking their schedules, and deciding whether the app is appropriate for their situation. Meduler is not a medical device, does not provide medical advice, and should not be the only safeguard for an important medication. The free experience and any Plus offering are subject to the product details shown publicly at the time of use. If a subscription becomes available, billing, renewal, cancellation, and applicable consumer rights are presented through the relevant purchase flow. Users must not misuse the service, attempt unauthorized access, or use it to expose another person’s information. Read these terms together with the privacy policy and medical disclaimer, and contact Meduler if a provision is unclear.',
  },
  {
    path: '/privacy',
    title: 'Privacy Policy | Meduler',
    description: "Meduler's Privacy Policy explains how we collect, use, and protect your personal health data. We are committed to your privacy.",
    priority: 0.4,
    changefreq: 'yearly',
    h1: 'Privacy Policy',
    intro: 'Your privacy is fundamental to how we build Meduler. This policy explains what data we collect, how it is used, and the strong protections we put in place — including AES-256 encryption at rest.',
    content: 'Meduler’s privacy policy explains how the service handles information used to organize medication routines. The product is designed around local-first storage for core medication data and privacy-conscious operation. Information that a user chooses to provide or share should be limited to what is needed for the requested feature. Meduler does not sell health information to advertisers. Optional analytics are intended to use a pseudonymous identifier and users can manage analytics preferences. People can review the policy for details about collection, use, sharing, retention, security, and their choices, including export or deletion where applicable. Do not send medication details or other sensitive health information to a general support address unless the policy or team specifically asks for it. This policy does not change the need to consult a qualified professional about treatment or a local emergency service about urgent care. Contact the privacy channel with policy questions.',
  },
  {
    path: '/disclaimer',
    title: 'Medical Disclaimer | Meduler',
    description: "Meduler's medical disclaimer. Meduler is a personal health management tool and is not a substitute for professional medical advice.",
    priority: 0.3,
    changefreq: 'yearly',
    h1: 'Medical Disclaimer',
    intro: 'Meduler is a personal organization tool designed to help you track medications. It is not a medical device and does not provide medical advice. Always consult your healthcare provider for medical guidance.',
    content: 'Meduler is a personal organization and reminder tool. It can help a user record medication information, arrange a schedule, and review dose history, but it cannot determine what treatment is right, confirm that a prescription is correct, or guarantee that a reminder will arrive. Information shown in the app should be checked against instructions from a doctor or pharmacist. Do not use Meduler as a substitute for professional medical advice, diagnosis, treatment, or an emergency response system. If you may be experiencing an emergency, contact local emergency services immediately. Users are responsible for entering accurate information, keeping device and notification settings working, and maintaining a backup method for critical medication timing. Caregiver sharing can support coordination but does not create a clinical relationship or transfer responsibility for treatment decisions. Read this disclaimer before relying on the app and ask a qualified healthcare professional when you have medical questions.',
  },
  {
    path: '/settings',
    title: 'Settings | Meduler',
    description: 'Manage your Meduler account settings.',
    priority: 0.1,
    changefreq: 'never',
    noindex: true,
  },
  {
    path: '/settings/profiles',
    title: 'Profiles | Meduler Settings',
    description: 'Manage profiles in Meduler.',
    priority: 0.1,
    changefreq: 'never',
    noindex: true,
  },
  {
    path: '/settings/preferences',
    title: 'Preferences | Meduler Settings',
    description: 'Manage preferences in Meduler.',
    priority: 0.1,
    changefreq: 'never',
    noindex: true,
  },
  {
    path: '/settings/privacy',
    title: 'Privacy Settings | Meduler',
    description: 'Manage privacy settings in Meduler.',
    priority: 0.1,
    changefreq: 'never',
    noindex: true,
  },
  {
    path: '/analytics',
    title: 'Analytics | Meduler',
    description: 'View your medication analytics.',
    priority: 0.1,
    changefreq: 'never',
    noindex: true,
  },
  {
    path: '/calendar',
    title: 'Calendar | Meduler',
    description: 'View your medication calendar.',
    priority: 0.1,
    changefreq: 'never',
    noindex: true,
  },
  {
    path: '/insights',
    title: 'Insights | Meduler',
    description: 'View your medication insights.',
    priority: 0.1,
    changefreq: 'never',
    noindex: true,
  },
  {
    path: '/upgrade',
    title: 'Upgrade | Meduler',
    description: 'Upgrade to Meduler Plus.',
    priority: 0.1,
    changefreq: 'never',
    noindex: true,
  },
  {
    path: '/debugger',
    title: 'Debugger | Meduler',
    description: 'Debug tools.',
    priority: 0.1,
    changefreq: 'never',
    noindex: true,
  },
  {
    path: '/offline',
    title: 'Offline | Meduler',
    description: 'Offline page.',
    priority: 0.1,
    changefreq: 'never',
    noindex: true,
  },
  {
    path: '/hook-testing-hub',
    title: 'Hook Testing Hub | Meduler',
    description: 'Hook testing tools.',
    priority: 0.1,
    changefreq: 'never',
    noindex: true,
  },
  {
    path: '/test-hooks',
    title: 'Test Hooks | Meduler',
    description: 'Test hooks page.',
    priority: 0.1,
    changefreq: 'never',
    noindex: true,
  },
  {
    path: '/hook-testing',
    title: 'Hook Testing Dashboard | Meduler',
    description: 'Internal hook testing dashboard.',
    priority: 0.1,
    changefreq: 'never',
    noindex: true,
  },
  {
    path: '/index-test',
    title: 'Index Component Test | Meduler',
    description: 'Internal index component test.',
    priority: 0.1,
    changefreq: 'never',
    noindex: true,
  },
  {
    path: '/a11y-dialog-test',
    title: 'Accessibility Dialog Test | Meduler',
    description: 'Internal accessibility dialog test.',
    priority: 0.1,
    changefreq: 'never',
    noindex: true,
  },
  {
    path: '/email-test',
    title: 'Email Test | Meduler',
    description: 'Internal email test page.',
    priority: 0.1,
    changefreq: 'never',
    noindex: true,
  },
  {
    path: '/landing',
    title: 'Legacy Landing Page | Meduler',
    description: 'Legacy Meduler landing page.',
    priority: 0.1,
    changefreq: 'never',
    noindex: true,
  },
];

export function getPublicRoutes() {
  return ROUTE_REGISTRY.filter((route) => !route.noindex);
}

export function isPublicRoute(path) {
  const route = ROUTE_REGISTRY.find((r) => r.path === path);
  return route ? !route.noindex : false;
}

export function isPrivateRoute(urlPath) {
  return ROUTE_REGISTRY.some(
    (r) => r.noindex && (urlPath === r.path || urlPath.startsWith(r.path + '/'))
  );
}

export function getRouteMetadata(path) {
  return ROUTE_REGISTRY.find((r) => r.path === path);
}
